import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Upload, X } from "lucide-react";
import { getSafeErrorMessage } from "@/lib/errorUtils";
import { Progress } from "@/components/ui/progress";

interface GalleryItem {
  id: string;
  title: string;
  category: string;
  type: string;
  image_url: string | null;
  video_url: string | null;
  display_order: number;
  is_active: boolean;
}

const GalleryManagement = () => {
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [titlePrefix, setTitlePrefix] = useState("");
  const [bulkCategory, setBulkCategory] = useState("ppf");

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate("/auth");
    }
    if (isAdmin) {
      fetchItems();
    }
  }, [isAdmin, loading, navigate]);

  const fetchItems = async () => {
    const { data, error } = await supabase
      .from("gallery_items")
      .select("*")
      .order("display_order", { ascending: true });

    if (error) {
      toast({
        title: "Error loading items",
        description: getSafeErrorMessage(error),
        variant: "destructive",
      });
    } else {
      setItems(data || []);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    setUploading(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("gallery")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("gallery").getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: getSafeErrorMessage(error),
        variant: "destructive",
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleAddItem = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const title = formData.get("title") as string;
    const category = formData.get("category") as string;
    const type = formData.get("type") as string;
    const videoUrl = formData.get("videoUrl") as string;

    let imageUrl = null;
    const imageFile = formData.get("image") as File;
    if (imageFile && imageFile.size > 0) {
      imageUrl = await handleImageUpload({
        target: { files: [imageFile] },
      } as any);
    }

    const { error } = await supabase.from("gallery_items").insert({
      title,
      category,
      type,
      image_url: imageUrl,
      video_url: type === "video" ? videoUrl : null,
      display_order: items.length,
    });

    if (error) {
      toast({
        title: "Error adding item",
        description: getSafeErrorMessage(error),
        variant: "destructive",
      });
    } else {
      toast({ title: "Item added successfully!" });
      fetchItems();
      e.currentTarget.reset();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("gallery_items")
      .delete()
      .eq("id", id);

    if (error) {
      toast({
        title: "Error deleting item",
        description: getSafeErrorMessage(error),
        variant: "destructive",
      });
    } else {
      toast({ title: "Item deleted successfully!" });
      fetchItems();
    }
  };

  const generateTitle = (filename: string, prefix: string): string => {
    // Remove file extension
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
    // Replace underscores and hyphens with spaces
    let title = nameWithoutExt.replace(/[_-]/g, " ");
    // Capitalize first letter of each word
    title = title.replace(/\b\w/g, (char) => char.toUpperCase());
    // Add prefix if provided
    return prefix ? `${prefix}${title}` : title;
  };

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const files = Array.from(e.target.files);
    setSelectedFiles(files);

    // Generate previews
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviews(newPreviews);
  };

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    
    // Revoke the URL to free memory
    URL.revokeObjectURL(previews[index]);
    
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  const uploadSingleFile = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("gallery")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("gallery").getPublicUrl(filePath);
      return data.publicUrl;
    } catch (error) {
      throw error;
    }
  };

  const handleBulkUpload = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select images to upload",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });

    const results = { success: 0, failed: 0, errors: [] as string[] };

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      try {
        // Upload file to storage
        const imageUrl = await uploadSingleFile(file);

        if (!imageUrl) {
          throw new Error("Failed to get public URL");
        }

        // Generate title from filename
        const title = generateTitle(file.name, titlePrefix);

        // Insert into database
        const { error: insertError } = await supabase.from("gallery_items").insert({
          title,
          category: bulkCategory,
          type: "image",
          image_url: imageUrl,
          display_order: items.length + i,
        });

        if (insertError) throw insertError;

        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push(`${file.name}: ${getSafeErrorMessage(error)}`);
      }

      setUploadProgress({ current: i + 1, total: selectedFiles.length });
    }

    // Show completion summary
    if (results.failed === 0) {
      toast({
        title: "Upload complete!",
        description: `Successfully uploaded ${results.success} images`,
      });
    } else {
      toast({
        title: "Upload completed with errors",
        description: `${results.success} succeeded, ${results.failed} failed`,
        variant: "destructive",
      });
    }

    // Reset form and refresh
    setUploading(false);
    setSelectedFiles([]);
    setPreviews([]);
    setUploadProgress({ current: 0, total: 0 });
    setTitlePrefix("");
    fetchItems();
  };

  if (loading || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-darker-bg to-background">
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center gap-4 mb-8">
          <Button asChild variant="outline">
            <Link to="/admin">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
          </Button>
          <h1 className="text-4xl font-heading font-bold">Gallery Management</h1>
        </div>

        <div className="grid gap-8">
          {/* Bulk Upload Section */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Bulk Upload Images
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bulkCategory">Category *</Label>
                  <select
                    id="bulkCategory"
                    value={bulkCategory}
                    onChange={(e) => setBulkCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground"
                  >
                    <option value="ppf">Paint Protection Film</option>
                    <option value="ceramic">Ceramic Coating</option>
                    <option value="tint">Window Tint</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="titlePrefix">Title Prefix (Optional)</Label>
                  <Input
                    id="titlePrefix"
                    value={titlePrefix}
                    onChange={(e) => setTitlePrefix(e.target.value)}
                    placeholder="PPF Project - "
                    className="bg-background"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulkImages">Select Multiple Images *</Label>
                <Input
                  id="bulkImages"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={handleFileSelection}
                  className="bg-background"
                  disabled={uploading}
                />
                <p className="text-sm text-muted-foreground">
                  Select multiple images (JPG, PNG, WEBP). HEIC not supported - convert first.
                </p>
              </div>

              {selectedFiles.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      {selectedFiles.length} file{selectedFiles.length !== 1 ? "s" : ""} selected
                    </p>
                    {!uploading && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedFiles([]);
                          setPreviews([]);
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                  </div>

                  {/* File Previews Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto p-2 bg-background/50 rounded-lg">
                    {previews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={selectedFiles[index].name}
                          className="w-full aspect-square object-cover rounded-md border border-border"
                        />
                        {!uploading && (
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {generateTitle(selectedFiles[index].name, titlePrefix)}
                        </p>
                      </div>
                    ))}
                  </div>

                  {uploading && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>
                          Uploading {uploadProgress.current} of {uploadProgress.total}...
                        </span>
                        <span className="text-muted-foreground">
                          {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                        </span>
                      </div>
                      <Progress
                        value={(uploadProgress.current / uploadProgress.total) * 100}
                        className="h-2"
                      />
                    </div>
                  )}

                  <Button
                    variant="premium"
                    className="w-full"
                    onClick={handleBulkUpload}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : `Upload ${selectedFiles.length} Image${selectedFiles.length !== 1 ? "s" : ""}`}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Single Item Upload (Keep existing functionality) */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Add Single Gallery Item
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddItem} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    required
                    placeholder="Luxury Sports Car - Full PPF"
                    className="bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    name="category"
                    required
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground"
                  >
                    <option value="ppf">Paint Protection Film</option>
                    <option value="ceramic">Ceramic Coating</option>
                    <option value="tint">Window Tint</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <select
                    id="type"
                    name="type"
                    required
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground"
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">Upload Image</Label>
                  <Input
                    id="image"
                    name="image"
                    type="file"
                    accept="image/*"
                    className="bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="videoUrl">Video URL (YouTube/Vimeo Embed)</Label>
                  <Input
                    id="videoUrl"
                    name="videoUrl"
                    placeholder="https://www.youtube.com/embed/..."
                    className="bg-background"
                  />
                </div>

                <Button type="submit" variant="premium" className="w-full" disabled={uploading}>
                  {uploading ? "Uploading..." : "Add Gallery Item"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Existing Items List */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Existing Gallery Items ({items.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-background rounded-lg border border-border"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {item.category} • {item.type}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GalleryManagement;
